import React, {type MouseEventHandler, type ReactElement, useEffect, useState} from "react";

interface ModalProps {
  children: ReactElement;
  title: string;
  body: string;
  onContinue?: MouseEventHandler | undefined;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  disabled?: boolean;
  id?: string;
  showOnTabClose?: boolean;
}

export const Modal = ({
  children,
  id = "modal",
  title,
  body,
  onContinue,
  primaryButtonText = "Save",
  secondaryButtonText = "Cancel",
  disabled,
  showOnTabClose
}: ModalProps) => {
  const [modalBackdropTimeout, setModalBackdropTimeout] = useState(undefined);
  useEffect(() => {
    const modal = document.querySelectorAll("[data-toggle*=modal]");

    if (!modal || modal.length === 0) {
      return;
    }

    const onClick = (e: Event) => {
      if (disabled) {
        return;
      }

      const openModal = document.querySelectorAll(".modal.show");
      const modalInner = document.querySelector(".modal-dialog");
      if ((e.target as Element).closest("[data-toggle*=modal]")) {
        const modal = (e.target as Element)
          .closest("[data-toggle*=modal]")
          ?.getAttribute?.("data-target");
        showModal(String(modal).replace("#", ""));
      } else if (
        (e.target as Element).getAttribute("data-dismiss") === "modal" ||
        ((e.target as Element).parentNode as Element)?.getAttribute?.("data-dismiss") === "modal" ||
        !modalInner?.contains(e.target as Element)
      ) {
        dismissModal(openModal);
      }
    };

    window.addEventListener("click", onClick);

    let modalOpenTimeout: NodeJS.Timeout;
    let showModalTimeout: NodeJS.Timeout;

    const dismissModal = (m: NodeListOf<Element>) => {
      for (let i = 0; i < m.length; i++) {
        const body = document.body;
        m[i].classList.remove("show");
        m[i].removeAttribute("aria-modal");
        m[i].setAttribute("aria-hidden", "true");
        modalOpenTimeout = setTimeout(function () {
          body.classList.remove("modal-open");
          (m[i] as HTMLElement).style.display = "none";
        }, 150);
      }

      const modalBackdrop = document.getElementById("modal-backdrop");
      modalBackdrop?.setAttribute("class", "modal-backdrop fade");
      modalBackdrop?.remove();
    };
    const showModal = (m: string) => {
      const modalElement = document.getElementById(m);
      const body = document.body;
      const hypotheticModalBackdrop = document.getElementById("modal-backdrop");

      if (hypotheticModalBackdrop) {
        return;
      } else {
        const modalBackdrop = document.createElement("div");
        modalBackdrop.setAttribute("id", "modal-backdrop");
        modalBackdrop.setAttribute("class", "modal-backdrop fade show");
        document?.getElementById("App")?.appendChild(modalBackdrop);
      }

      body.classList.add("modal-open");
      modalElement.style.display = "block";
      showModalTimeout = setTimeout(function () {
        modalElement?.classList.add("show");
        modalElement?.removeAttribute("aria-hidden");
        modalElement?.setAttribute("aria-modal", "true");
      }, 150);
    };

    const beforeUnload = event => {
      const confirmationMessage = "Are you sure you want to leave?";
      event.returnValue = confirmationMessage; // For IE and Firefox
      return confirmationMessage; // For Safari, Chrome and Opera
    };

    !disabled && showOnTabClose && window.addEventListener("beforeunload", beforeUnload);

    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("beforeunload", beforeUnload);
      clearTimeout(modalOpenTimeout);
      clearTimeout(modalBackdropTimeout);
      clearTimeout(showModalTimeout);
    };
  }, [showOnTabClose, disabled]);

  useEffect(() => {
    clearTimeout(modalBackdropTimeout);
  }, [modalBackdropTimeout]);

  const dismissModalBackdrop = () => {
    setModalBackdropTimeout(
      setTimeout(function () {
        const modalBackdrop = document.getElementById("modal-backdrop");
        modalBackdrop?.setAttribute("class", "modal-backdrop fade");
        modalBackdrop?.remove();
      }, 600)
    );
  };

  return (
    <>
      {React.cloneElement(children, {"data-toggle": "modal", "data-target": `#${id}`})}
      <div
        className="modal fade"
        id={id}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title" id="exampleModalLabel">
                {title}
              </h3>
              <button type="button" className="btn-close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">{body}</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-tertiary" data-dismiss="modal">
                {secondaryButtonText}
              </button>
              <button
                type="button"
                onClick={event => {
                  dismissModalBackdrop();
                  onContinue(event);
                }}
                className="btn btn-primary"
                data-dismiss="modal">
                {primaryButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
